import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Guitar } from 'lucide-react';
import { post } from '../lib/api';
export function AuthPage({ mode }: { mode: 'login' | 'register' | 'forgot' | 'reset' }) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    const fd = new FormData(e.currentTarget);
    try {
      if (mode === 'login') {
        await post('/auth/login', { email: fd.get('email'), password: fd.get('password') });
        await client.invalidateQueries({ queryKey: ['me'] });
        navigate('/dashboard');
      } else if (mode === 'register') {
        await post('/auth/register', {
          email: fd.get('email'),
          displayName: fd.get('displayName'),
          password: fd.get('password'),
        });
        await client.invalidateQueries({ queryKey: ['me'] });
        navigate('/verify-email');
      } else if (mode === 'forgot') {
        await post('/auth/forgot-password', { email: fd.get('email') });
        setSent(true);
      } else {
        await post('/auth/reset-password', {
          token: fd.get('token'),
          password: fd.get('password'),
        });
        navigate('/login');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }
  const titles = {
    login: 'Welcome back',
    register: 'Join the songbook',
    forgot: 'Reset your password',
    reset: 'Choose a new password',
  };
  return (
    <div className="auth-page">
      <section className="auth-panel">
        <Link className="brand" to="/">
          <Guitar />
          ChordVerse
        </Link>
        <div>
          <span className="kicker">{mode === 'register' ? 'Free forever' : 'Keep playing'}</span>
          <h1>{titles[mode]}</h1>
          <p>
            {sent
              ? 'If an account exists, a reset link is on its way.'
              : mode === 'login'
                ? 'Log in to save favorites and share chord sheets.'
                : mode === 'register'
                  ? 'Share your knowledge with players around the world.'
                  : 'We’ll send a secure, one-hour reset link.'}
          </p>
          {!sent && (
            <form onSubmit={submit}>
              {mode === 'register' && (
                <label>
                  Display name
                  <input name="displayName" required minLength={2} autoComplete="name" />
                </label>
              )}
              {mode !== 'reset' && (
                <label>
                  Email
                  <input name="email" type="email" required autoComplete="email" />
                </label>
              )}
              {mode === 'reset' && (
                <label>
                  Reset token
                  <input name="token" required />
                </label>
              )}
              {mode !== 'forgot' && (
                <label>
                  Password
                  <input
                    name="password"
                    type="password"
                    required
                    minLength={12}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  />
                  <small>At least 12 characters</small>
                </label>
              )}
              {error && (
                <div className="form-error" role="alert">
                  {error}
                </div>
              )}
              <button className="button wide">
                {mode === 'login'
                  ? 'Log in'
                  : mode === 'register'
                    ? 'Create account'
                    : mode === 'forgot'
                      ? 'Send reset link'
                      : 'Reset password'}
              </button>
            </form>
          )}
          {(mode === 'login' || mode === 'register') && (
            <>
              <div className="auth-divider">
                <span>or</span>
              </div>
              <a className="button secondary wide google-login" href="/api/v1/auth/google">
                Continue with Google
              </a>
            </>
          )}
          <div className="auth-links">
            {mode === 'login' && (
              <>
                <Link to="/forgot-password">Forgot password?</Link>
                <span>
                  New here? <Link to="/register">Create an account</Link>
                </span>
              </>
            )}
            {mode === 'register' && (
              <span>
                Already a member? <Link to="/login">Log in</Link>
              </span>
            )}
          </div>
        </div>
      </section>
      <aside className="auth-art">
        <blockquote>“The best songbook is the one we write together.”</blockquote>
        <div className="floating-chord">
          G<small>320003</small>
        </div>
        <div className="floating-chord second">
          Em<small>022000</small>
        </div>
      </aside>
    </div>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const client = useQueryClient();
  const [message, setMessage] = useState(
    token ? 'Verifying your email…' : 'Check your inbox for a verification link.',
  );
  const [working, setWorking] = useState(Boolean(token));

  useEffect(() => {
    if (!token) return;
    void post('/auth/verify-email', { token })
      .then(async () => {
        await client.invalidateQueries({ queryKey: ['me'] });
        setMessage('Email verified. Redirecting to your dashboard…');
        setTimeout(() => navigate('/dashboard'), 800);
      })
      .catch((error: unknown) =>
        setMessage(error instanceof Error ? error.message : 'Verification failed.'),
      )
      .finally(() => setWorking(false));
  }, [token, client, navigate]);

  return (
    <div className="auth-page">
      <section className="auth-panel">
        <Link className="brand" to="/">
          <Guitar /> ChordVerse
        </Link>
        <div>
          <span className="kicker">Account security</span>
          <h1>Verify your email</h1>
          <p>{message}</p>
          {!token && (
            <button
              className="button wide"
              disabled={working}
              onClick={async () => {
                setWorking(true);
                try {
                  await post('/auth/resend-verification');
                  setMessage('A new verification email has been sent.');
                } catch (error) {
                  setMessage(error instanceof Error ? error.message : 'Unable to send email.');
                } finally {
                  setWorking(false);
                }
              }}
            >
              {working ? 'Sending…' : 'Resend verification email'}
            </button>
          )}
        </div>
      </section>
      <div className="auth-art" />
    </div>
  );
}
